/* Dependencies */
import axios, { AxiosError } from 'axios';

// Utils
import { cartNotification } from '../../Utils/CartNotification/CartNotification';
import { Product } from '../../Widgets/CoffeePicker/CoffeePicker.model';

// Models
import { AddToCartRequest, ICollection } from './ShopifyService.model';

// Create the shipify client
// @ts-ignore
import ShopifyClient from 'shopify-buy/index.unoptimized.umd';

/**
 * Shopify Service
 * @class
 */
class ShopifyService {
  /**
   * Add an item to the cart.
   * @param data - request data to be sent.
   * @returns
   */
  addToCart(data: AddToCartRequest): Promise<boolean> {
    return new Promise((resolve, reject) => {
      axios
        .post('/cart/add.js', data)
        .then((response) => {
          const { items } = response.data;
          // Render success message
          cartNotification.showCartSuccess({
            productTitle: items[0].product_title,
            productQuantity: data.items[0].quantity,
            productVariant: items[0].variant_options
              ? items[0].variant_options.join(', ')
              : '',
          });
          resolve(true);
        })
        .catch((error: AxiosError) => {
          // Pull the error message
          if (
            error.response &&
            error.response.data &&
            error.response.data.description
          ) {
            const { description } = error.response.data;
            cartNotification.showCartError(description);
          }
          // Default error message
          else {
            cartNotification.showCartError(
              'Unhandled error has occured, please try again and contact support if this persists.'
            );
          }

          resolve(false);
        });
    });
  }

  /**
   * Get collection from Shopify API
   * @param collectionId - collection id.
   * @returns
   */
  getCollection(collectionId: string): Promise<Product[]> {
    return new Promise((resolve, reject) => {
      // Check if its the production site.
      let isProduction = false;
      if (
        window.location.hostname.includes('ozonecoffee.co.uk') ||
        window.location.hostname.includes('hasbean.myshopify.com')
      ) {
        isProduction = true;
      }

      // Store the domain
      let domain = window.location.hostname;

      // Handle local development
      if (domain.includes('127.0.0.1')) {
        domain = 'hasbean-dev.myshopify.com';
      }

      // Create Storefront API instance
      const client = ShopifyClient.buildClient({
        domain,
        storefrontAccessToken: isProduction
          ? 'e7d8539e1ebef15519f370947ce7ca2f'
          : '1df74b04101b80fb3d87ad65844e1cf7',
      });

      // Query for all products in coffee collection
      const productsQuery = client.graphQLClient.query((root: any) => {
        root.addConnection(
          'collections',
          { args: { first: 1, query: collectionId } },
          (collection: any) => {
            collection.add('title');
            collection.addConnection(
              'products',
              { args: { first: 250, filters: { available: true } } },
              (product: any) => {
                product.add('title');
                product.add('handle');
                product.add('id');
                product.add('productType');
                product.addConnection(
                  'images',
                  { args: { first: 1 } },
                  (featuredImage: any) => {
                    featuredImage.add('url');
                    featuredImage.add('altText');
                  }
                );

                product.add(
                  'metafield',
                  {
                    args: {
                      key: 'flavour_profile_category',
                      namespace: 'my_fields',
                    },
                    alias: 'flavour_profile_category',
                  },
                  (metafield: any) => {
                    metafield.add('value');
                  }
                );

                product.add(
                  'metafield',
                  {
                    args: {
                      key: 'flavour_profile',
                      namespace: 'my_fields',
                    },
                    alias: 'flavour_profile',
                  },
                  (metafield: any) => {
                    metafield.add('value');
                  }
                );

                product.add(
                  'metafield',
                  {
                    args: {
                      key: 'process',
                      namespace: 'my_fields',
                    },
                    alias: 'process',
                  },
                  (metafield: any) => {
                    metafield.add('value');
                  }
                );

                product.add(
                  'metafield',
                  {
                    args: {
                      key: 'varietal',
                      namespace: 'my_fields',
                    },
                    alias: 'varietal',
                  },
                  (metafield: any) => {
                    metafield.add('value');
                  }
                );
                product.add(
                  'metafield',
                  {
                    args: {
                      key: 'roast',
                      namespace: 'my_fields',
                    },
                    alias: 'roast',
                  },
                  (metafield: any) => {
                    metafield.add('value');
                  }
                );
                product.add(
                  'metafield',
                  {
                    args: {
                      key: 'type',
                      namespace: 'my_fields',
                    },
                    alias: 'metatype',
                  },
                  (metafield: any) => {
                    metafield.add('value');
                  }
                );

                product.add(
                  'metafield',
                  {
                    args: {
                      key: 'farm_name',
                      namespace: 'my_fields',
                    },
                    alias: 'farm_name',
                  },
                  (metafield: any) => {
                    metafield.add('value');
                  }
                );
                product.add(
                  'metafield',
                  {
                    args: {
                      key: 'origin',
                      namespace: 'my_fields',
                    },
                    alias: 'origin',
                  },
                  (metafield: any) => {
                    metafield.add('value');
                  }
                );
                product.add('priceRange', {}, (priceRange: any) => {
                  priceRange.add(
                    'minVariantPrice',
                    {},
                    (minVariantPrice: any) => {
                      minVariantPrice.add('amount');
                    }
                  );
                  priceRange.add(
                    'maxVariantPrice',
                    {},
                    (maxVariantPrice: any) => {
                      maxVariantPrice.add('amount');
                    }
                  );
                });
                product.add('compareAtPriceRange', {}, (priceRange: any) => {
                  priceRange.add(
                    'minVariantPrice',
                    {},
                    (minVariantPrice: any) => {
                      minVariantPrice.add('amount');
                    }
                  );
                  priceRange.add(
                    'maxVariantPrice',
                    {},
                    (maxVariantPrice: any) => {
                      maxVariantPrice.add('amount');
                    }
                  );
                });
                product.add('publishedAt');
                product.add('options', {}, (productOptions: any) => {
                  productOptions.add('name');
                  productOptions.add('values');
                });
                product.addConnection(
                  'variants',
                  { args: { first: 100 } },
                  (variants: any) => {
                    variants.add('id');
                    variants.add('availableForSale');
                    variants.add('priceV2', {}, (price: any) => {
                      price.add('amount');
                    });
                    variants.add(
                      'compareAtPriceV2',
                      {},
                      (compareAtPrice: any) => {
                        compareAtPrice.add('amount');
                      }
                    );
                    variants.add(
                      'selectedOptions',
                      {},
                      (selectedOptions: any) => {
                        selectedOptions.add('name');
                        selectedOptions.add('value');
                      }
                    );
                  }
                );
              }
            );
          }
        );
      });

      // Send the query & format the results
      client.graphQLClient.send(productsQuery).then((model: any) => {
        const collections = model.model.collections as Array<ICollection>;

        let products: Product[] = collections[0].products.map((product) => {
          let flavourProfile = product.flavour_profile_category?.value;

          // Set a default flavour profile if none is found
          if (!flavourProfile) {
            flavourProfile = 'Cocoa';
          }

          return {
            featured: null,
            productType: product.productType,
            bestselling: null,
            handle: product.handle,
            id: product.id,
            title: product.title,
            featuredImage: product.images[0]?.url,
            featuredImageAlt: product.images[0]?.altText,
            farmName: product.farm_name?.value,
            flavourNotes: product.flavour_profile?.value,
            flavourProfile,
            process: product.process?.value,
            varietal: product.varietal?.value,
            roast: product.roast?.value,
            type: product.metatype?.value,
            origin: product.origin?.value,
            price: Number(product.compareAtPriceRange.minVariantPrice.amount),
            price_varies:
              Number(product.priceRange.minVariantPrice.amount) !==
              Number(product.priceRange.maxVariantPrice.amount),
            dateAdded: product.publishedAt,
            options: product.options.map((y) => {
              const values = y.values.map((p: any) => {
                return p.value;
              });

              return {
                name: y.name,
                values,
                selectedValue: values[0],
              };
            }),
            variants: product.variants.map((v) => {
              return {
                id: v.id,
                availableForSale: v.availableForSale,
                price: v.priceV2.amount,
                selectedOptions: v.selectedOptions.map((so) => {
                  return { name: so.name, values: so.value };
                }),
              };
            }),
          };
        });
        resolve(products);
      });
    });
  }
}

// Export the service
export const shopifyService = new ShopifyService();
